// @systeme: primitives-ecran
// ============================================================================
// PairsForm — useConfirm : confirmer un geste, demander un texte (lot 7.4)
// ============================================================================
// Remplace `window.confirm` / `window.prompt` (88 + 8 sites, décision Q3 du
// 2026-10-17) : UN dialog MUI monté par `ConfirmProvider` (App.tsx), une
// promesse résolue au clic.
//   const { confirmer, demanderTexte } = useConfirm();
//   if (!(await confirmer("Supprimer ce groupe ?"))) return;
//   const motif = await demanderTexte("Motif du refus :");   // null = annulé
// Options (titre, libellés, `danger`, saisie obligatoire / multiligne / date)
// dans `components/ui/ConfirmDialog`. Une demande qui arrive pendant qu'une
// autre attend annule la première (double clic) : jamais deux dialogs.
// ============================================================================

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ConfirmDialog, SaisieDialog, type OptionsConfirmation, type OptionsSaisie,
} from "../components/ui/ConfirmDialog";

export type { OptionsConfirmation, OptionsSaisie };

export interface Confirmation {
  /** Résout `true` si la personne confirme, `false` si elle annule ou ferme. */
  confirmer: (options: OptionsConfirmation | string) => Promise<boolean>;
  /** Résout le texte saisi, `null` si la personne annule (contrat de window.prompt). */
  demanderTexte: (options: OptionsSaisie | string, valeurInitiale?: string) => Promise<string | null>;
}

type Demande =
  | { type: "confirmation"; options: OptionsConfirmation; resoudre: (confirme: boolean) => void }
  | { type: "saisie"; options: OptionsSaisie; resoudre: (texte: string | null) => void };

const ContexteConfirmation = createContext<Confirmation | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [demande, setDemande] = useState<Demande | null>(null);
  const enCours = useRef<Demande | null>(null);

  const ouvrir = useCallback((nouvelle: Demande) => {
    const precedente = enCours.current;
    if (precedente) {
      if (precedente.type === "confirmation") precedente.resoudre(false);
      else precedente.resoudre(null);
    }
    enCours.current = nouvelle;
    setDemande(nouvelle);
  }, []);

  const fermer = useCallback(() => {
    enCours.current = null;
    setDemande(null);
  }, []);

  const confirmer = useCallback<Confirmation["confirmer"]>(
    (options) =>
      new Promise<boolean>((resoudre) => {
        ouvrir({
          type: "confirmation",
          options: typeof options === "string" ? { message: options } : options,
          resoudre,
        });
      }),
    [ouvrir],
  );

  const demanderTexte = useCallback<Confirmation["demanderTexte"]>(
    (options, valeurInitiale) =>
      new Promise<string | null>((resoudre) => {
        ouvrir({
          type: "saisie",
          options: typeof options === "string" ? { message: options, valeurInitiale } : options,
          resoudre,
        });
      }),
    [ouvrir],
  );

  const valeur = useMemo(() => ({ confirmer, demanderTexte }), [confirmer, demanderTexte]);

  return (
    <ContexteConfirmation.Provider value={valeur}>
      {children}
      <ConfirmDialog
        ouvert={demande?.type === "confirmation"}
        options={demande?.type === "confirmation" ? demande.options : null}
        onReponse={(confirme) => {
          if (demande?.type === "confirmation") demande.resoudre(confirme);
          fermer();
        }}
      />
      <SaisieDialog
        ouvert={demande?.type === "saisie"}
        options={demande?.type === "saisie" ? demande.options : null}
        onReponse={(texte) => {
          if (demande?.type === "saisie") demande.resoudre(texte);
          fermer();
        }}
      />
    </ContexteConfirmation.Provider>
  );
}

export function useConfirm(): Confirmation {
  const ctx = useContext(ContexteConfirmation);
  if (!ctx) throw new Error("useConfirm doit être appelé sous <ConfirmProvider>");
  return ctx;
}
